<template>
  <div class="order-wrapper">
    <portal target-el="#filter">
      <!-- Add filter -->
      <Header v-if="filter.status"
              placeholder="Search by Invoice No"
              title="Invoice"
              :statusList="statusList"
              :filter="filter"
              :loading="isLoading"
              :inXLSProgress="inXLSProgress"
              sampleFileName ="payment-status-upload.xlsx"
              :selectedInvoices="selectedInvoices.length"
              :rejectedCount="rejectedCount"
              :showVoucherView="showVoucherView"
              @search="search"
              @voucher="voucher"
              @approve="approve"
              @reject="reject"
              @exportInvoice="exportInvoice"
              @exportAllInvoice="exportAllInvoice"
              @changeMainStatus="changeMainStatus"
              @changeStatus="changeStatus"
              @changeType="changeType"
              @changeSearchTerm="changeSearchTerm"
              @changeDateRange="changeDateRange"
              @reApprove="reApprove"
              @bulkUpload="bulkUpload"
              @rejectDownload="rejectDownload" />
  </portal>
    <div>

      <div class="sublevel">
        <FilterDropdown
          v-if="!showVoucherView"
          :options="invoiceTypes"
          :selected="selectedInvoiceType"
          @updateOption="onChangeInvoiceTypes"
        />
      </div>
      <div v-if="isLoading">
        <Spinner />
      </div>
      <div v-else>
        <Heading :columns="columnHeaders" @selectAll="selectAll" />

        <div class="fixed-row" v-if="showVoucherView"
          v-infinite-scroll="loadMore"
          infinite-scroll-disabled="loadCheck"
          infinite-scroll-throttle-delay="400"
          infinite-scroll-distance="10">
          <VoucherRow
            v-for="invoice in invoices"
            :invoice="invoice"
            :selected="selectedInvoices"
            :voucher_updated_dates="voucher_updated_dates"
            :paymentUpdatedDates="paymentUpdatedDates"
            :commentLoading="commentLoading"
            :comments="comments"
            @addConversation="addConversation"
            @getComments="getComments"
            @addInvoice="addInvoice"
            @showDates="showDates"
            :key="invoice.invoice_id"
          />
        </div>

        <div class="fixed-row top-margin" :class="{'with-sublevel' : $hasPermission('view_wallet') }"
          v-if="!showVoucherView"
          v-infinite-scroll="loadMore"
          infinite-scroll-disabled="loadCheck"
          infinite-scroll-throttle-delay="400"
          infinite-scroll-distance="10">
          <InvoiceRow
            v-for="invoice in invoices"
            :invoice="invoice"
            :selected="selectedInvoices"
            :customFields="customFields"
            :voucherVendorId="voucherForVendorId"
            :paymentUpdatedDates="paymentUpdatedDates"
            :voucherForTax="voucherForTax"
            :commentLoading="commentLoading"
            :comments="comments"
            @addConversation="addConversation"
            @getComments="getComments"
            @addInvoice="addInvoice"
            :key="invoice.invoice_id"
          />
        </div>
        <div v-if="isPaginating">
          <Spinner />
        </div>
        <EmptyState v-if="!invoices.length" icon="mdm" :text="Messages.ERR_NO_RECORDS_FOUND" />
      </div>
    </div>
  </div>
</template>
<script>
import debounce from 'debounce';
import { mapState, mapGetters } from 'vuex';
import { invoiceHeading, voucherHeading, invoiceTypes } from '@/constants/invoice';
import EmptyState from '@/components/common/EmptyState';
import FilterDropdown from '@common/components/FilterDropdown';
import Messages from '@/constants/messages';

import Header from './header';
import Heading from './heading';
import InvoiceRow from './invoice-row';
import VoucherRow from './voucher-row';

export default {
  components: {
    Header,
    Heading,
    InvoiceRow,
    VoucherRow,
    EmptyState,
    FilterDropdown,
  },
  data() {
    return {
      selectedInvoices: [],
      invoiceHeading,
      voucherHeading,
      voucherForVendorId: null,
      voucherForTax: null,
      rejectedCount: null,
      loadCheck: true,
      invoiceTypes,
      Messages,
      withStatusExport: true,
      actionInProgress: false,
    };
  },
  computed: {
    ...mapState('invoice', [
      'isLoading',
      'isPaginating',
      'inXLSProgress',
      'filter',
      'customFields',
      'hasEmpty',
      'statusList',
      'transporterAll',
      'invoices',
      'paymentUpdatedDates',
      'voucher_updated_dates',
      'commentLoading',
      'comments',
      'selectedInvoiceType',
    ]),
    ...mapGetters({
      configurations: 'app/configurations',
    }),
    columnHeaders() {
      let columns = this.invoiceHeading;
      if (this.showVoucherView) {
        columns = this.voucherHeading;
      }
      return columns;
    },
    showVoucherView() {
      return this.$hasFeature('invoice_voucher') && this.$invoice.voucherView();
    },
  },
  created() {
    if (!this.filter.status) {
      this.initializeData();
    } else {
      this.$store.commit('invoice/setPage', 1);
      this.loadMore();
    }
    this.$store.dispatch('invoice/customFields');
  },
  methods: {
    onChangeInvoiceTypes(data) {
      this.$store.commit('invoice/setInvoiceType', data);
      this.$store.commit('invoice/setPage', 1);
      this.loadMore();
    },
    selectAll(value) {
      if (value) {
        if (
          this.$hasFeature('invoice_voucher') &&
          this.filter.status === 3 &&
          this.filter.mainStatus === 1
        ) {
          const [firstInvoice] = this.invoices;
          this.voucherForVendorId = firstInvoice.transporter.id
            ? firstInvoice.transporter.id
            : 0;
          this.voucherForTax = firstInvoice.gst_type
            ? firstInvoice.gst_type
            : 0;
          const invoiceList = this.invoices.filter(
            data =>
              data.transporter_revision === false &&
              data.gst_type === this.voucherForTax &&
              data.transporter.id === this.voucherForVendorId,
          );
          this.selectedInvoices = invoiceList.map(order => order.id);
        } else {
          this.selectedInvoices = this.invoices.map(order => order.id);
        }
      } else {
        this.selectedInvoices = [];
      }
    },
    async initializeData() {
      try {
        await this.$store.dispatch('invoice/transporters');
        const accessStatus = await this.$store.dispatch('invoice/statusList');
        if (accessStatus.length) {
          const [firstStatus] = accessStatus;
          const [firstSubStatus] = firstStatus.subFilter.length
            ? firstStatus.subFilter
            : {};
          const mainId = firstStatus.id ? firstStatus.id : 0;
          this.$store.commit('invoice/setMainStatus', mainId);
          const savedFilter = localStorage.getItem('invoice_filter');
          const subId = firstSubStatus.id ? firstSubStatus.id : 0;
          this.$store.commit('invoice/setSubStatus', subId);
          localStorage.setItem('invoice_filter', subId);
          this.loadMore();
        }
      } catch (error) {
        // this.$error({ message: hasData.error });
      }
    },
    constructParams(data) {
      let result = {};
      const dataKeys = Object.keys(data);
      dataKeys.forEach((key) => {
        if (data[key] !== null && !['mainStatus'].includes(key)) {
          result[key] = data[key];
        }
      });
      if (
        this.$hasFeature('invoice_voucher') &&
        this.filter.status === 3 &&
        this.filter.mainStatus === 1
      ) {
        result = Object.assign(result, { voucher: 'pending' });
      }
      if (this.filter.mainStatus === 2) {
        result = Object.assign(result, { type: 'voucher' });
      } else {
        result = Object.assign(result, { type: 'invoice' });

        if (this.selectedInvoiceType.id) {
          result = Object.assign(result, { filter_type: this.selectedInvoiceType.id });
        }
      }
      return result;
    },
    async loadMore() {
      try {
        if (this.filter.status) {
          const params = this.constructParams(this.filter);
          const hasData = await this.$store.dispatch('invoice/search', params);
          if (hasData.error) {
            this.$error({ message: hasData.error });
          }
          this.loadCheck = this.hasEmpty;
        }
      } catch (error) {
        // this.$error({ message: hasData.error });
      }
    },
    changeMainStatus(id) {
      this.$store.commit('invoice/setMainStatus', id);
    },
    changeStatus(id) {
      this.$store.commit('invoice/setSubStatus', id);
      localStorage.setItem('invoice_filter', id);
      this.resetData();
    },
    changeSearchTerm(data) {
      this.$store.commit('invoice/setSearchTerm', data);
      this.resetData();
    },
    async showDates(referenceId) { // invoice or voucher Id
      try {
        const hasData = await this.$store.dispatch('invoice/showUpdatedDates', { referenceId });
        if (hasData.error) {
          this.$error({ message: hasData.error });
        }
      } catch (error) {
        // this.$error({ message: hasData.error });
      }
    },
    changeType(data) {
      if (data.id === null) {
        this.$store.commit('invoice/setGstType', null);
        this.$store.commit('invoice/setNoteType', null);
      } else if (data.type === 'gst_type') {
        this.$store.commit('invoice/setGstType', data.id);
        this.$store.commit('invoice/setNoteType', null);
      } else {
        this.$store.commit('invoice/setGstType', null);
        this.$store.commit('invoice/setNoteType', data.id);
      }
      this.resetData();
    },
    changeDateRange(date) {
      this.$store.commit('invoice/setDateRange', date);
      this.resetData();
    },
    resetData() {
      this.selectedInvoices = [];
      this.voucherForVendorId = null;
      this.voucherForTax = null;
      this.$store.commit('invoice/setPage', 1);
      this.loadMore();
    },
    addInvoice(invoice) {
      const index = this.selectedInvoices.indexOf(invoice.id);
      if (index > -1) {
        this.selectedInvoices.splice(index, 1);
      } else {
        this.selectedInvoices.push(invoice.id);
      }
      if (
        this.$hasFeature('invoice_voucher') &&
        invoice.status === 3 &&
        this.filter.mainStatus === 1
      ) {
        if (this.selectedInvoices.length === 0) {
          this.voucherForVendorId = null;
          this.voucherForTax = null;
        } else if (this.selectedInvoices.length === 1) {
          this.voucherForVendorId = invoice.transporter.id;
          this.voucherForTax = invoice.gst_type;
        }
      }
    },
    exportAllInvoice() {
      this.withStatusExport = false;
      this.exportInvoice('exportInvoice');
    },
    async exportInvoice(type) {
      try {
        let section = 'exportInvoice';
        let exportFilter = {};
        const exportTypeConfig = this.configurations &&
          this.configurations.paid_invoice_export_type ?
          this.configurations.paid_invoice_export_type : '';
        if (type === 'Miro' && exportTypeConfig === 'miro') {
          section = 'exportMiro';
          exportFilter = (this.filter.gst_type) ? { type: 'gst', value: this.filter.gst_type } : { type: 'note', value: this.filter.note_type };
        }
        let params = {
          ids: this.selectedInvoices,
          exportFilter,
        };
        if (this.withStatusExport) {
          params = Object.assign(params, { status: this.filter.status });
        }
        if (this.filter.date_range) {
          params = Object.assign(params, { date_range: this.filter.date_range });
        }
        const result = await this.$store.dispatch(`invoice/${section}`, params);
        this.selectedInvoices = [];
        if (result) {
          this.$success({ message: this.$getSuccess(result) });
        }
        if (result.error) {
          this.$error({ message: result.error });
        }
        this.withStatusExport = true;
      } catch (error) {
        // this.$error({ message: hasData.error });
      }
    },
    async confirmationBox(text, buttonText) {
      return this.$PromptConfirm(text, 'Confirmation', {
        confirmButtonText: buttonText,
        showCancelButton: true,
        lockScroll: true,
        showClose: false,
        center: true,
      });
    },
    async doMyAction(actionFor, successText, params = {}) {
      try {
        const passParams = Object.assign(
          { ids: this.selectedInvoices },
          params,
        );
        if(!this.actionInProgress) {
          this.actionInProgress = true;
          const result = await this.$store.dispatch(
            `invoice/${actionFor}`,
            passParams,
          );
          if (result.success || result.voucher_number) {
            let Addtext = '';
            if (result.voucher_number) {
              Addtext = result.voucher_number;
            }
            this.$success({
              message: `${Addtext} ${successText}`,
            });
            this.$store.commit('invoice/setPage', 1);
            this.selectedInvoices = [];
            this.voucherForTax = null;
            this.voucherForVendorId = null;
            this.actionInProgress = false;
            this.loadMore();
          }
          if (result.error) {
            this.actionInProgress = true;
            this.$error({ message: result.error });
          }
        }
      } catch (error) {
        // this.$error({ message: hasData.error });
      }
    },
    async reApprove() {
      try {
        const confirmApprove = await this.confirmationBox(
          this.$getConst('INVOICE_REAPPROVE_PROMPT'),
          'Re Approve',
        );
        if (confirmApprove === 'confirm') {
          this.doMyAction('reapprove', this.$getSuccess('INVOICE_REAPPROVE'));
        }
      } catch (e) {
        // console.log(e.response);
      }
    },
    async voucher() {
      try {
        const confirmApprove = await this.confirmationBox(
          this.$getConst('GENERATE_VOUCHER_PROMPT'),
          'Generate',
        );
        if (confirmApprove === 'confirm') {
          this.doMyAction(
            'genrateVoucher',
            this.$getSuccess('GENERATE_VOUCHER'),
          );
        }
      } catch (e) {
        // console.log(e.response);
      }
    },
    async approve(actionBy) {
      // librarian or accountant verify or Approve
      try {
        let action = actionBy;
        let promptText =
          actionBy === 'librarian'
            ? this.$getConst('INVOICE_VERIFY_PROMPT')
            : this.$getConst('INVOICE_APPROVE_PROMPT');
        let successText =
          actionBy === 'librarian'
            ? this.$getSuccess('INVOICE_VERIFY')
            : this.$getSuccess('INVOICE_APPROVE');

        const buttontext = actionBy === 'librarian' ? 'Verify' : 'Approve';
        if (this.$hasFeature('invoice_voucher')) {
          promptText =
            actionBy === 'librarian'
              ? this.$getConst('VOUCHER_VERIFY_PROMPT')
              : this.$getConst('VOUCHER_APPROVE_PROMPT');
          successText =
            actionBy === 'librarian'
              ? this.$getSuccess('VOUCHER_VERIFY')
              : this.$getSuccess('VOUCHER_APPROVE');

          action = `${actionBy}/voucher`;
        }
        const confirmApprove = await this.confirmationBox(
          promptText,
          buttontext,
        );
        if (confirmApprove === 'confirm') {
          this.doMyAction('updateStatus', successText, { type: 1, action });
        }
      } catch (e) {
        // console.log(e.response);
      }
    },
    async reject(actionBy) {
      try {
        let action = actionBy;
        let promptText =
          actionBy === 'librarian'
            ? this.$getConst('INVOICE_VERIFY_PROMPT_REJECT')
            : this.$getConst('INVOICE_APPROVE_PROMPT_REJECT');
        let successText =
          actionBy === 'librarian'
            ? this.$getSuccess('INVOICE_HOLD')
            : this.$getSuccess('INVOICE_REJECTED_SSC');
        const buttontext = actionBy === 'librarian' ? 'Hold' : 'Reject';
        if (this.$hasFeature('invoice_voucher')) {
          promptText =
            actionBy === 'librarian'
              ? this.$getConst('VOUCHER_VERIFY_PROMPT_REJECT')
              : this.$getConst('VOUCHER_APPROVE_PROMPT_REJECT');
          successText =
            actionBy === 'librarian'
              ? this.$getSuccess('VOUCHER_VERIFY_REJECT')
              : this.$getSuccess('VOUCHER_APPROVE_REJECT');

          action = `${actionBy}/voucher`;
        }
        const confirmApprove = await this.confirmationBox(
          promptText,
          buttontext,
        );
        if (confirmApprove === 'confirm') {
          this.doMyAction('updateStatus', successText, { type: 2, action });
        }
      } catch (e) {
        // console.log(e.response);
      }
    },
    async bulkUpload(data) {
      try {
        const formData = new FormData();
        formData.append('attachment', data);
        const result = await this.$store.dispatch(
          'invoice/bulkUpload',
          formData,
        );
        if (result.success) {
          this.uploadMiroStatus();
        } else {
          this.$error({ message: result.error });
        }
      } catch (e) {
        // console.log(e.response);
      }
    },
    async uploadMiroStatus() {
      try {
        const result = await this.$store.dispatch('invoice/uploadStatus');
        if (result.code && result.code !== 'PROGRESS') {
          if (result.code === 'COMPLETED') {
            const text = this.$getSuccess('BULK_MIRO_COUNT', {
              firstArgument: result.valid_count,
              secondArgument: result.invalid_count,
              thirdArgument: result.valid_count + result.invalid_count,
            });
            this.rejectedCount = result.invalid_count;
            this.$success({ message: text });
            this.$store.commit('invoice/setPage', 1);
            this.loadMore();
          } else {
            this.$error({ message: this.$getErr('BULK_UPLOAD_MIRO_FAILS') });
          }
        } else if (result.error) {
          this.$error({ message: result.error });
        } else {
          setTimeout(() => {
            this.uploadMiroStatus();
          }, 6000);
        }
      } catch (error) {
        // this.$error({ message: hasData.error });
      }
    },
    async rejectDownload() {
      try {
        const result = await this.$store.dispatch('invoice/rejectDownload');
        if (result.error) {
          this.$error({ message: result.error });
        }
      } catch (e) {
        // console.log(e.response);
      }
    },

    search: debounce(function debounceSearch(query) {
      // @param  {String} query
      this.selectedInvoices = [];
      const searchQuery = query.length ? query : null;
      this.$store.commit('invoice/setSearchQuery', searchQuery);
      this.$store.commit('invoice/setPage', 1);
      this.loadMore();
    }, 500),

    async addConversation(data) {
      try {
        const result = await this.$store.dispatch('invoice/addComment', {
          data,
        });
        if (result.error) {
          this.$error({ message: result.error });
        }
      } catch (e) {
        // console.log(e.response);
      }
    },

    async getComments(data) {
      try {
        const result = await this.$store.dispatch('invoice/comments', { data });
        if (result.error) {
          this.$error({ message: result.error });
        }
      } catch (e) {
        // console.log(e.response);
      }
    },
  },
};
</script>
<style lang='scss'>
.order {
  &-wrapper-margin {
    min-height: 700px;
    margin-top: 50px;
  }
}
.fixed-row {
  position: fixed;
  padding-right: 10px;
  top: 220px;
  left: 20px;
  right: 10px;
  bottom: 0;
  overflow-x: hidden;
  overflow-y: auto;
  &.top-margin {
    top: 220px;
  }
  &.with-sublevel {
    top: 260px;
  }
}
.comment-height {
  height: 180px;
  overflow: scroll;
}
</style>
